/**
 * Référentiels partagés (région, niveau d'étude, secteur…) en attendant l'intégration du
 * referential-service. Libellés français de la maquette ; codes stables envoyés au backend.
 * Utilisé par l'inscription (register) et l'édition de profil (espace-jeune/profile).
 */
/** Plateforme mono-pays (YouthConnekt Cameroun) : aucun sélecteur de pays dans la maquette, mais profile-service exige countryCode pour valoriser le bonus de complétude "localisation" (voir ProfileService#recalculateAndSave). */
export const DEFAULT_COUNTRY_CODE = 'CM';

export const REGION_CODES: Record<string, string> = {
  Adamaoua: 'ADAMAOUA',
  Centre: 'CENTER',
  Est: 'EAST',
  'Extrême-Nord': 'FAR_NORTH',
  Littoral: 'LITTORAL',
  Nord: 'NORTH',
  'Nord-Ouest': 'NORTH_WEST',
  Ouest: 'WEST',
  Sud: 'SOUTH',
  'Sud-Ouest': 'SOUTH_WEST',
};

export const LANGUAGE_CODES: Record<string, string> = {
  Français: 'fr',
  Anglais: 'en',
  Bilingue: 'fr',
};

export const GENRE_OPTIONS = ['Masculin', 'Féminin'];
export const LANGUE_OPTIONS = ['Français', 'Anglais', 'Bilingue'];
export const REGION_OPTIONS = Object.keys(REGION_CODES);

export const NIVEAU_OPTIONS = [
  'Sans diplôme',
  'CEP',
  'BEPC / CAP',
  'Probatoire',
  'Baccalauréat',
  'BTS / DUT',
  'Licence',
  'Master',
  'Doctorat',
];

export const SECTOR_OPTIONS = [
  'Agro-industrie',
  'Numérique',
  'Énergie',
  'Santé',
  'Artisanat',
  'Transport',
  'Commerce',
];

/** Codes envoyés à application-service (CreateApplicationRequest.sectorCode / regionCode déjà couvert par REGION_CODES). */
export const SECTOR_CODES: Record<string, string> = {
  'Agro-industrie': 'AGRO',
  Numérique: 'DIGITAL',
  Énergie: 'ENERGY',
  Santé: 'HEALTH',
  Artisanat: 'CRAFTS',
  Transport: 'TRANSPORT',
  Commerce: 'TRADE',
};

export const STATUT_OPTIONS = [
  'Élève / Étudiant',
  "En recherche d'emploi",
  'En emploi',
  'Entrepreneur',
  'Autre',
];

export const COMPETENCE_OPTIONS = [
  'Informatique / Bureautique',
  'Langues',
  'Gestion de projet',
  'Communication',
  'Marketing digital',
  'Artisanat',
  'Agriculture',
  'Design / Créatif',
];

export const RECHERCHE_OPTIONS = ['Emploi', 'Stage', 'Formation', 'Financement', 'Mentorat', 'Réseautage'];

/** Options propres à la section "Projet / entrepreneuriat" du profil (maquette Espace Jeune.dc.html). */
export const PROJECT_STATUT_OPTIONS = [
  'Étudiant(e)',
  'Sans emploi',
  'Salarié(e)',
  'Porteur de projet / entrepreneur',
  'Entrepreneur(e) établi(e)',
];
export const STADE_OPTIONS = ['Idée', 'Prototype / MVP', 'Lancé, premiers clients', 'En croissance'];

/** Codes envoyés à profile-service (YoungProfileUpsertRequest.gender). */
export const GENDER_CODES: Record<string, string> = {
  Masculin: 'MALE',
  Féminin: 'FEMALE',
};

/** Codes envoyés à profile-service (YoungProfileUpsertRequest.educationLevelCode). */
export const EDUCATION_LEVEL_CODES: Record<string, string> = {
  'Sans diplôme': 'NONE',
  CEP: 'CEP',
  'BEPC / CAP': 'BEPC_CAP',
  Probatoire: 'PROBATOIRE',
  Baccalauréat: 'BAC',
  'BTS / DUT': 'BTS_DUT',
  Licence: 'LICENCE',
  Master: 'MASTER',
  Doctorat: 'DOCTORAT',
};
export const EDUCATION_LEVEL_OPTIONS = Object.keys(EDUCATION_LEVEL_CODES);

/** Codes envoyés à profile-service (YoungProfileUpsertRequest.employmentStatus), mêmes libellés que PROJECT_STATUT_OPTIONS. */
export const EMPLOYMENT_STATUS_CODES: Record<string, string> = {
  'Étudiant(e)': 'STUDENT',
  'Sans emploi': 'UNEMPLOYED',
  'Salarié(e)': 'EMPLOYED',
  'Porteur de projet / entrepreneur': 'ENTREPRENEUR_STARTING',
  'Entrepreneur(e) établi(e)': 'ENTREPRENEUR_ESTABLISHED',
};

/** Codes envoyés à profile-service (YoungProfileUpsertRequest.entrepreneurshipStage), mêmes libellés que STADE_OPTIONS. */
export const ENTREPRENEURSHIP_STAGE_CODES: Record<string, string> = {
  Idée: 'IDEATION',
  'Prototype / MVP': 'PROTOTYPE',
  'Lancé, premiers clients': 'LAUNCHED',
  'En croissance': 'GROWTH',
};

/** Niveaux envoyés pour une compétence (ProfileSkillCreateRequest.level, enum SkillLevel backend). */
export const SKILL_LEVEL_CODES: Record<string, string> = {
  Débutant: 'BEGINNER',
  Intermédiaire: 'INTERMEDIATE',
  Avancé: 'ADVANCED',
  Expert: 'EXPERT',
};
export const SKILL_LEVEL_OPTIONS = Object.keys(SKILL_LEVEL_CODES);

/** Types envoyés pour un besoin (ProfileNeedCreateRequest.needType, enum NeedType backend — les 9 valeurs). */
export const NEED_TYPE_CODES: Record<string, string> = {
  Formation: 'TRAINING',
  Mentorat: 'MENTORSHIP',
  Financement: 'FUNDING',
  'Accès au marché': 'MARKET_ACCESS',
  Réseautage: 'NETWORKING',
  'Accompagnement juridique': 'LEGAL_SUPPORT',
  'Accompagnement numérique': 'DIGITAL_SUPPORT',
  Équipement: 'EQUIPMENT',
  Visibilité: 'VISIBILITY',
};
export const NEED_TYPE_OPTIONS = Object.keys(NEED_TYPE_CODES);

/** Codes envoyés à profile-service (OrganizationProfileUpsertRequest.organizationType, enum OrganizationType backend). */
export const ORGANIZATION_TYPE_CODES: Record<string, string> = {
  Partenaire: 'PARTNER',
  Institution: 'INSTITUTION',
  Startup: 'STARTUP',
  TPME: 'TPME',
  Association: 'ASSOCIATION',
  'Centre de formation': 'TRAINING_CENTER',
  Incubateur: 'INCUBATOR',
  'Bailleur de fonds': 'DONOR',
};
export const ORGANIZATION_TYPE_OPTIONS = Object.keys(ORGANIZATION_TYPE_CODES);

/**
 * Codes envoyés à opportunity-service (CreateOpportunityRequest.typeCode). Aucun groupe
 * référentiel "OPPORTUNITY_TYPE" n'est encore seedé côté referential-service : ces codes
 * reprennent la convention déjà utilisée par le site public (voir TYPE_TONE_MAP dans
 * shared/utils/opportunity-card.mapper.ts) pour que les opportunités créées ici gardent
 * le bon visuel (couleur/pictogramme) une fois publiées.
 */
export const OPPORTUNITY_TYPE_CODES: Record<string, string> = {
  Emploi: 'emploi',
  Formation: 'formation',
  Financement: 'financement',
  Innovation: 'innovation',
  Citoyenneté: 'citoyennete',
};
export const OPPORTUNITY_TYPE_OPTIONS = Object.keys(OPPORTUNITY_TYPE_CODES);

/** Codes envoyés à profile-service (ProfileExperienceCreateRequest.experienceType, enum ExperienceType backend). */
export const EXPERIENCE_TYPE_CODES: Record<string, string> = {
  Formation: 'EDUCATION',
  Emploi: 'EMPLOYMENT',
  Entrepreneuriat: 'ENTREPRENEURSHIP',
  Bénévolat: 'VOLUNTEERING',
  Certification: 'CERTIFICATION',
  Projet: 'PROJECT',
};
export const EXPERIENCE_TYPE_OPTIONS = Object.keys(EXPERIENCE_TYPE_CODES);

/**
 * Consentements gérés depuis le profil (UC-08 : "Couvre… les consentements du profil"), enum
 * ConsentType côté backend (profile-service). Libellés + explication courte pour l'écran.
 */
export const CONSENT_TYPE_LABELS: Record<
  'DATA_PROCESSING' | 'PUBLIC_VISIBILITY' | 'COMMUNICATION' | 'MATCHING' | 'STORYTELLING_CONTACT',
  { label: string; description: string }
> = {
  DATA_PROCESSING: {
    label: 'Traitement de mes données',
    description: 'Nécessaire pour utiliser votre profil sur la plateforme.',
  },
  PUBLIC_VISIBILITY: {
    label: 'Visibilité publique du profil',
    description: "Permet aux partenaires et à d'autres jeunes de consulter votre profil.",
  },
  COMMUNICATION: {
    label: 'Communications',
    description: 'Recevoir des messages et notifications de la plateforme.',
  },
  MATCHING: {
    label: 'Matching et recommandations',
    description: 'Utiliser votre profil pour vous proposer des opportunités et contacts pertinents.',
  },
  STORYTELLING_CONTACT: {
    label: 'Storytelling et témoignages',
    description: 'Être contacté(e) pour partager votre expérience en success story.',
  },
};
export const CONSENT_TYPE_ORDER = [
  'DATA_PROCESSING',
  'PUBLIC_VISIBILITY',
  'COMMUNICATION',
  'MATCHING',
  'STORYTELLING_CONTACT',
] as const;

/** Suggestions de tags pour les sections compétences / centres d'intérêt du profil. */
export const SKILL_SUGGESTIONS = [
  'Marketing digital',
  'Comptabilité',
  'Anglais',
  'Développement web',
  'Négociation',
  'Gestion agricole',
];
export const INTEREST_SUGGESTIONS = [
  'Innovation sociale',
  'Énergies vertes',
  'Numérique',
  'Emploi jeunes',
  'Coopératives',
];
